/**
 * The asset's Sharing tab (0.31.0): its share links (who made them, expiry, views; revoke; for administrators the view
 * history, 1.6.3) and its approvals
 * (workflow steps, share links and people asked), each with its decision and thread of notes. Timecoded notes
 * seek the player next to it.
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Eye, History, Link2, ShieldOff } from 'lucide-react';
import type { AssetDetail, ShareLinkDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useApprovals, useMe, useShares } from '../api/hooks';
import { toast } from '../components/Toasts';
import { ShareViewsModal } from '../components/ShareViewsModal';
import { dateTime, relative } from '../lib/format';
import { ApprovalStatusIcon, ApprovalThread, DecisionBox, approverLabel, requestMessage, statusLabel, timingOf } from './Approvals';
import { Thread } from '../components/Notes';
import { queryKeys } from '../api/queryKeys';

export function SharingTab({ asset, currentSeconds, onSeek }: { asset: AssetDetail; currentSeconds: number; onSeek: (seconds: number) => void }) {
  const shares = useShares(asset.id);
  const approvals = useApprovals({ assetId: asset.id, status: 'all', scope: 'all' });
  const timing = useMemo(() => timingOf(asset), [asset]);
  const links = shares.data?.items ?? [];
  const items = approvals.data?.items ?? [];
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="sharing-tab">
      <section>
        <p className="section-label">Links</p>
        {links.length === 0 ? <p className="faint" style={{ margin: 0 }}>No share links. Use Share above to make one.</p>
          : links.map((s) => <ShareRow key={s.id} s={s} />)}
      </section>
      <section>
        <p className="section-label">Approvals</p>
        {items.length === 0 ? <p className="faint" style={{ margin: 0 }}>Nobody has been asked to approve this asset.</p> : items.map((a) => (
          <div key={a.id} className="panel-card" style={{ marginBottom: 10, padding: 12 }} data-testid="asset-approval">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <ApprovalStatusIcon status={a.status} />
              <b data-testid="asset-approval-status">{statusLabel(a)}</b>
              <span className="muted">{approverLabel(a)}</span>
              {a.decidedBy && <span className="muted">· {a.decidedBy}{a.decidedExternally ? ' (via link)' : ''}</span>}
              <span style={{ flex: 1 }} />
              <span className="faint" style={{ fontSize: 12 }}>{a.source === 'flow' ? a.run?.definitionName : a.source === 'share' ? 'Share link' : `Asked by ${a.requestedBy ?? 'someone'}`} · {relative(a.createdAt)}</span>
            </div>
            {a.canDecide && <DecisionBox a={a} />}
            {open === a.id || a.notes <= 3
              ? <ApprovalThread approval={a} timing={timing} assetId={asset.id} currentSeconds={asset.media.proxyUrl ? currentSeconds : undefined} onSeek={onSeek} />
              : <>
                {requestMessage(a) && <Thread messages={[requestMessage(a)!]} loaded />}
                <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(a.id)}>Show {a.notes} notes</button>
              </>}
          </div>
        ))}
      </section>
    </div>
  );
}

function ShareRow({ s }: { s: ShareLinkDTO }) {
  const qc = useQueryClient();
  // 1.6.3: administrators see each link's view history.
  const isAdmin = useMe().data?.permissions.includes('admin') ?? false;
  const [history, setHistory] = useState(false);
  const revoke = async () => {
    if (!window.confirm('Revoke this link? It stops working at once, also for anyone watching now.')) return;
    try {
      await api(`/api/v1/shares/${s.id}/revoke`, { method: 'POST' });
      toast('Link revoked');
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.shares(s.assetId) }), qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() })]);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  return (
    <div className="loc-row" style={{ flexWrap: 'wrap', marginBottom: 6 }} data-testid="share-row" data-state={s.state}>
      <Link2 size={13} />
      <span className="mono" title="The start of the link">/s/{s.tokenHint}…</span>
      <span className={`pill ${s.state === 'active' ? 'accent' : ''}`} data-testid="share-state">{s.state === 'active' ? 'Active' : s.state === 'expired' ? 'Expired' : 'Revoked'}</span>
      {s.allowDownload && <span className="pill" title="Allows downloading the proxy"><Download size={10} style={{ marginRight: 3 }} />Download</span>}
      {s.approval && <span className="pill" title="Asks for approval"><ApprovalStatusIcon status={s.approval.status} /></span>}
      <span className="key" style={{ fontFamily: 'inherit' }}>
        {s.createdBy ?? 'Someone'} · {relative(s.createdAt)} · {s.state === 'revoked' ? `revoked ${s.revokedBy ? `by ${s.revokedBy} ` : ''}${relative(s.revokedAt)}` : s.expiresAt ? `${s.state === 'expired' ? 'expired' : 'expires'} ${dateTime(s.expiresAt)}` : 'never expires'}
      </span>
      <span className="faint" title={s.lastViewedAt ? `Last opened ${dateTime(s.lastViewedAt)}` : 'Not opened yet'}><Eye size={11} /> {s.views}</span>
      {isAdmin && <button className="btn ghost sm" onClick={() => setHistory(true)} data-testid="share-history"><History size={12} /> History</button>}
      {s.canRevoke && <button className="btn ghost sm danger" onClick={() => void revoke()} data-testid="share-revoke"><ShieldOff size={12} /> Revoke</button>}
      {history && <ShareViewsModal share={s} onClose={() => setHistory(false)} />}
    </div>
  );
}
